import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, tokens } from '@/design';
import {
  DEFAULT_LAYOUT_TEXT_SYMBOL,
  normalizeLegendElement,
  type LegendPropertiesPatch,
} from './layoutDocument';
import { LayoutTextSymbolPanel } from './LayoutTextSymbolPanel';
import { SymbolColorPicker } from './SymbolColorPicker';
import type {
  LegendElement,
  LegendFittingStrategy,
  LegendItemArrangement,
  LegendItemOverride,
  LegendItemType,
  LegendLayerItem,
  LegendTextPart,
  MapFrameElement,
} from './layoutTypes';

export interface LegendPropertiesPanelProps {
  readonly legend: LegendElement;
  readonly items: readonly LegendLayerItem[];
  readonly frames: readonly MapFrameElement[];
  readonly onChange: (patch: LegendPropertiesPatch) => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}

type PanelView = 'legend' | 'item' | 'text';

const TEXT_PARTS: readonly { readonly id: LegendTextPart; readonly label: string }[] = [
  { id: 'title', label: 'Title' },
  { id: 'groupLayerName', label: 'Group Layer Name' },
  { id: 'layerName', label: 'Layer Name' },
  { id: 'heading', label: 'Heading' },
  { id: 'label', label: 'Label' },
  { id: 'description', label: 'Description' },
];

const FITTING: readonly {
  readonly id: LegendFittingStrategy;
  readonly label: string;
}[] = [
  { id: 'adjustFontSize', label: 'Adjust font size' },
  { id: 'adjustColumns', label: 'Adjust columns' },
  { id: 'adjustColumnsAndFont', label: 'Adjust columns and font size' },
  { id: 'adjustFrame', label: 'Adjust frame' },
  { id: 'manualColumns', label: 'Manual columns' },
];

const ARRANGEMENTS: readonly {
  readonly id: LegendItemArrangement;
  readonly label: string;
}[] = [
  { id: 'patchLabelDescription', label: 'Patch, label, description' },
  { id: 'patchDescriptionLabel', label: 'Patch, description, label' },
  { id: 'labelPatchDescription', label: 'Label, patch, description' },
  { id: 'labelDescriptionPatch', label: 'Label, description, patch' },
  { id: 'descriptionPatchLabel', label: 'Description, patch, label' },
  { id: 'descriptionLabelPatch', label: 'Description, label, patch' },
];

function bounded(
  raw: string,
  min: number,
  max: number,
  fallback: number,
): number {
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;
}

function Section({
  title,
  children,
  open = false,
}: {
  readonly title: string;
  readonly children: ReactNode;
  readonly open?: boolean;
}) {
  return (
    <details open={open}>
      <summary
        style={{
          minHeight: tokens.density.rowDense,
          cursor: 'pointer',
          fontSize: tokens.font.size.md,
          fontWeight: tokens.font.weight.semibold,
        }}
      >
        {title}
      </summary>
      <div
        style={{
          display: 'grid',
          gap: tokens.space[2],
          padding: `0 0 ${tokens.space[3]}px ${tokens.space[5]}px`,
        }}
      >
        {children}
      </div>
    </details>
  );
}

export function LegendPropertiesPanel({
  legend: sourceLegend,
  items,
  frames,
  onChange,
  onDelete,
  onClose,
}: LegendPropertiesPanelProps) {
  const legend = normalizeLegendElement(sourceLegend);
  const [view, setView] = useState<PanelView>('item');
  const [selectedItemId, setSelectedItemId] = useState(items[0]?.id ?? '');
  const [textPart, setTextPart] = useState<LegendTextPart>('label');
  const selectedItem =
    items.find((item) => item.id === selectedItemId) ?? items[0] ?? null;
  const override = selectedItem
    ? legend.itemOverrides?.[selectedItem.id] ?? {}
    : {};
  const includedIds = useMemo(
    () => new Set(legend.layerIds ?? items.map((item) => item.id)),
    [items, legend.layerIds],
  );

  const field: CSSProperties = {
    width: '100%',
    minHeight: tokens.density.rowDense,
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${tokens.density.touchMin * 2.8}px minmax(0, 1fr)`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const check: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const iconButton: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: tokens.color.ink.secondary,
    cursor: 'pointer',
  };
  const tab = (active: boolean): CSSProperties => ({
    minHeight: tokens.density.rowDefault,
    padding: `0 ${tokens.space[1]}px`,
    border: 0,
    borderBottom: `${tokens.focus.ringWidth}px solid ${
      active ? tokens.color.semantic.info : 'transparent'
    }`,
    background: 'transparent',
    color: active ? tokens.color.semantic.info : tokens.color.ink.secondary,
    cursor: 'pointer',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.md,
  });
  const patchItem = (patch: LegendItemOverride) => {
    if (selectedItem) onChange({ layerId: selectedItem.id, item: patch });
  };
  const numberField = (
    label: string,
    value: number,
    min: number,
    max: number,
    onValue: (value: number) => void,
  ) => (
    <label style={row}>
      <span>{label}</span>
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        step={0.5}
        value={value}
        onChange={(event) =>
          onValue(bounded(event.target.value, min, max, value))
        }
        style={field}
      />
    </label>
  );
  const frameSymbol = (
    title: 'Background' | 'Border' | 'Shadow',
    key: 'background' | 'border' | 'shadow',
  ) => {
    const symbol = legend[key];
    return (
      <Section title={title}>
        <label style={check}>
          <input
            type="checkbox"
            aria-label={`Enable legend ${key}`}
            checked={symbol.enabled}
            onChange={(event) =>
              onChange({ [key]: { enabled: event.target.checked } })
            }
          />
          Enable {title.toLowerCase()}
        </label>
        <div style={row}>
          <span>Color</span>
          <SymbolColorPicker
            label={`Legend ${key} color`}
            value={symbol.color}
            allowNoColor={false}
            disabled={!symbol.enabled}
            onChange={(color) =>
              onChange({ [key]: { color: color ?? symbol.color } })
            }
          />
        </div>
        {key === 'border'
          ? numberField(
              'Legend border width',
              symbol.outlineWidthPt,
              0,
              12,
              (outlineWidthPt) => onChange({ border: { outlineWidthPt } }),
            )
          : null}
        {numberField(
          `Legend ${key} gap X`,
          symbol.gapXPt,
          0,
          36,
          (gapXPt) => onChange({ [key]: { gapXPt } }),
        )}
        {numberField(
          `Legend ${key} gap Y`,
          symbol.gapYPt,
          0,
          36,
          (gapYPt) => onChange({ [key]: { gapYPt } }),
        )}
      </Section>
    );
  };

  return (
    <aside
      data-testid="legend-properties-panel"
      aria-label="Legend properties"
      style={{
        position: 'absolute',
        zIndex: tokens.zIndex.dock,
        top: tokens.space[3],
        right: tokens.space[3],
        width: tokens.layout.popoverLg,
        maxHeight: `calc(100% - ${tokens.space[6]}px)`,
        overflowY: 'auto',
        padding: tokens.space[3],
        border: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[2],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: tokens.space[2],
        }}
      >
        <div>
          <div style={{ color: tokens.color.semantic.info, fontSize: tokens.font.size.lg }}>
            Element
          </div>
          <strong style={{ fontSize: tokens.font.size.md }}>{legend.name}</strong>
        </div>
        <div style={{ display: 'flex', gap: tokens.space[1] }}>
          <button
            type="button"
            aria-label="Delete legend"
            disabled={legend.locked}
            onClick={onDelete}
            style={{
              ...iconButton,
              color: legend.locked
                ? tokens.color.ink.muted
                : tokens.color.semantic.danger,
            }}
          >
            <Icon name="delete" size={20} decorative />
          </button>
          <button
            type="button"
            aria-label="Close legend properties"
            onClick={onClose}
            style={iconButton}
          >
            <Icon name="close" size={20} decorative />
          </button>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Legend property tabs"
        style={{ display: 'flex', gap: tokens.space[4], marginTop: tokens.space[2] }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={view === 'legend'}
          onClick={() => setView('legend')}
          style={tab(view === 'legend')}
        >
          Legend
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'item'}
          onClick={() => setView('item')}
          style={tab(view === 'item')}
        >
          Legend Item
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'text'}
          onClick={() => setView('text')}
          style={tab(view === 'text')}
        >
          Text Symbol
        </button>
      </div>

      {view === 'legend' ? (
        <div role="tabpanel" aria-label="Legend element options">
          <Section title="General" open>
            <label style={row}>
              <span>Name</span>
              <input
                aria-label="Legend name"
                value={legend.name}
                onChange={(event) => onChange({ name: event.target.value })}
                style={field}
              />
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Legend visible"
                checked={legend.visible}
                onChange={(event) => onChange({ visible: event.target.checked })}
              />
              Visible
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Legend locked"
                checked={legend.locked}
                onChange={(event) => onChange({ locked: event.target.checked })}
              />
              Locked
            </label>
          </Section>
          <Section title="Legend" open>
            <label style={row}>
              <span>Map Frame</span>
              <select
                aria-label="Legend map frame"
                value={legend.mapFrameId}
                onChange={(event) => onChange({ mapFrameId: event.target.value })}
                style={field}
              >
                {frames.map((frame, index) => (
                  <option key={frame.id} value={frame.id}>
                    {frames.length === 1 ? 'Map Frame' : `Map Frame ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Show legend title"
                checked={legend.showTitle}
                onChange={(event) => onChange({ showTitle: event.target.checked })}
              />
              Show title
            </label>
            <label style={row}>
              <span>Title</span>
              <input
                aria-label="Legend title"
                value={legend.title}
                onChange={(event) => onChange({ title: event.target.value })}
                style={field}
              />
            </label>
            <fieldset
              style={{
                display: 'grid',
                gap: tokens.space[1],
                margin: 0,
                padding: tokens.space[2],
                border: tokens.border.thin,
              }}
            >
              <legend>Included layers</legend>
              {items.map((item) => (
                <label key={item.id} style={check}>
                  <input
                    type="checkbox"
                    aria-label={`Include ${item.label} in legend`}
                    checked={includedIds.has(item.id)}
                    onChange={(event) => {
                      const next = new Set(includedIds);
                      if (event.target.checked) next.add(item.id);
                      else next.delete(item.id);
                      onChange({ layerIds: [...next] });
                    }}
                  />
                  {item.label}
                </label>
              ))}
            </fieldset>
          </Section>
          <Section title="Fitting Strategy" open>
            <label style={row}>
              <span>Strategy</span>
              <select
                aria-label="Legend fitting strategy"
                value={legend.fittingStrategy}
                onChange={(event) =>
                  onChange({
                    fittingStrategy: event.target.value as LegendFittingStrategy,
                  })
                }
                style={field}
              >
                {FITTING.map((strategy) => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.label}
                  </option>
                ))}
              </select>
            </label>
            {numberField(
              'Legend columns',
              legend.columnCount,
              1,
              8,
              (columnCount) => onChange({ columnCount: Math.round(columnCount) }),
            )}
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Equal legend column widths"
                checked={legend.equalColumnWidths}
                onChange={(event) =>
                  onChange({ equalColumnWidths: event.target.checked })
                }
              />
              Set equal widths
            </label>
            {numberField(
              'Legend minimum font size',
              legend.minimumFontSizePt,
              4,
              72,
              (minimumFontSizePt) => onChange({ minimumFontSizePt }),
            )}
          </Section>
          <Section title="Word Wrapping">
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Wrap legend labels"
                checked={legend.wrapLabels}
                onChange={(event) => onChange({ wrapLabels: event.target.checked })}
              />
              Labels
            </label>
            {numberField(
              'Legend label wrap width',
              legend.labelWrapWidthPt,
              12,
              720,
              (labelWrapWidthPt) => onChange({ labelWrapWidthPt }),
            )}
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Wrap legend descriptions"
                checked={legend.wrapDescriptions}
                onChange={(event) =>
                  onChange({ wrapDescriptions: event.target.checked })
                }
              />
              Descriptions
            </label>
            {numberField(
              'Legend description wrap width',
              legend.descriptionWrapWidthPt,
              12,
              720,
              (descriptionWrapWidthPt) => onChange({ descriptionWrapWidthPt }),
            )}
          </Section>
          <Section title="Spacing">
            {(
              [
                ['Title gap', 'titleGapPt'],
                ['Layer name gap', 'layerNameGapPt'],
                ['Heading gap', 'headingGapPt'],
                ['Class gap', 'classGapPt'],
                ['Patch-label gap', 'patchLabelGapPt'],
                ['Column gap', 'columnGapPt'],
              ] as const
            ).map(([label, key]) => (
              <div key={key}>
                {numberField(label, legend.spacing[key], 0, 72, (value) =>
                  onChange({ spacing: { [key]: value } }),
                )}
              </div>
            ))}
          </Section>
          {frameSymbol('Background', 'background')}
          {frameSymbol('Border', 'border')}
          {frameSymbol('Shadow', 'shadow')}
        </div>
      ) : null}

      {view === 'item' ? (
        <div role="tabpanel" aria-label="Legend item options">
          <label style={{ ...row, margin: `${tokens.space[3]}px 0` }}>
            <span>Legend item</span>
            <select
              aria-label="Selected legend item"
              value={selectedItem?.id ?? ''}
              onChange={(event) => setSelectedItemId(event.target.value)}
              style={field}
            >
              {items.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          {selectedItem ? (
            <>
              <Section title="Show" open>
                {(
                  [
                    ['Group layer name', 'showGroupLayerName', Boolean(selectedItem.groupLayerName)],
                    ['Layer name', 'showLayerName', true],
                    ['Headings', 'showHeading', Boolean(selectedItem.heading)],
                    ['Label (or layer name)', 'showLabel', true],
                    ['Descriptions', 'showDescription', Boolean(selectedItem.description)],
                    ['Feature count', 'showFeatureCount', selectedItem.featureCount != null],
                  ] as const
                ).map(([label, key, applicable]) => (
                  <label key={key} style={check}>
                    <input
                      type="checkbox"
                      aria-label={`Show legend ${label.toLowerCase()}`}
                      disabled={!applicable}
                      checked={
                        applicable
                        && (
                          override[key]
                          ?? (
                            key !== 'showDescription'
                            && key !== 'showFeatureCount'
                          )
                        )
                      }
                      onChange={(event) => patchItem({ [key]: event.target.checked })}
                    />
                    {label}
                  </label>
                ))}
              </Section>
              <Section title="Type" open>
                <label style={row}>
                  <span>Type</span>
                  <select
                    aria-label="Legend item type"
                    disabled={!selectedItem.rasterRamp}
                    value={override.type ?? 'default'}
                    onChange={(event) =>
                      patchItem({ type: event.target.value as LegendItemType })
                    }
                    style={field}
                  >
                    <option value="default">Default</option>
                    <option value="horizontal">Horizontal</option>
                    <option value="vertical">Vertical</option>
                  </select>
                </label>
              </Section>
              <Section title="Arrangement" open>
                <label style={row}>
                  <span>Arrangement</span>
                  <select
                    aria-label="Legend item arrangement"
                    value={override.arrangement ?? 'patchLabelDescription'}
                    onChange={(event) =>
                      patchItem({
                        arrangement:
                          event.target.value as LegendItemArrangement,
                      })
                    }
                    style={field}
                  >
                    {ARRANGEMENTS.map((arrangement) => (
                      <option key={arrangement.id} value={arrangement.id}>
                        {arrangement.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={check}>
                  <input
                    type="checkbox"
                    aria-label="Keep legend item in single column"
                    checked={override.keepInSingleColumn === true}
                    onChange={(event) =>
                      patchItem({ keepInSingleColumn: event.target.checked })
                    }
                  />
                  Keep in single column
                </label>
              </Section>
              <Section title="Sizing" open>
                {numberField(
                  'Legend patch width',
                  override.patchWidthPt ?? 24,
                  1,
                  288,
                  (patchWidthPt) => patchItem({ patchWidthPt }),
                )}
                {numberField(
                  'Legend patch height',
                  override.patchHeightPt ?? 12,
                  1,
                  288,
                  (patchHeightPt) => patchItem({ patchHeightPt }),
                )}
                <label style={check}>
                  <input
                    type="checkbox"
                    aria-label="Scale symbol to legend patch"
                    checked={override.scaleToPatch === true}
                    onChange={(event) =>
                      patchItem({ scaleToPatch: event.target.checked })
                    }
                  />
                  Scale to fit patch size
                </label>
              </Section>
              <Section title="Feature Display Options">
                <label style={check}>
                  <input
                    type="checkbox"
                    aria-label="Only show features visible in map extent"
                    disabled={!selectedItem.supportsVisibleExtent}
                    checked={override.onlyVisibleInExtent === true}
                    onChange={(event) =>
                      patchItem({ onlyVisibleInExtent: event.target.checked })
                    }
                  />
                  Only show features visible in the map extent
                </label>
              </Section>
              <Section title="Indents">
                {numberField(
                  'Legend layer name indent',
                  override.layerNameIndentPt ?? 2,
                  0,
                  144,
                  (layerNameIndentPt) => patchItem({ layerNameIndentPt }),
                )}
                {numberField(
                  'Legend heading indent',
                  override.headingIndentPt ?? 0,
                  0,
                  144,
                  (headingIndentPt) => patchItem({ headingIndentPt }),
                )}
                {numberField(
                  'Legend classes indent',
                  override.classIndentPt ?? 0,
                  0,
                  144,
                  (classIndentPt) => patchItem({ classIndentPt }),
                )}
                {legend.fittingStrategy === 'manualColumns'
                  ? numberField(
                      'Legend item column',
                      override.manualColumn ?? 1,
                      1,
                      legend.columnCount,
                      (manualColumn) =>
                        patchItem({ manualColumn: Math.round(manualColumn) }),
                    )
                  : null}
              </Section>
              <Section title="Labels">
                <label style={row}>
                  <span>Display name</span>
                  <input
                    aria-label={`${selectedItem.label} legend label`}
                    value={override.label ?? selectedItem.label}
                    onChange={(event) => patchItem({ label: event.target.value })}
                    style={field}
                  />
                </label>
                {selectedItem.rasterRamp ? (
                  <>
                    <label style={row}>
                      <span>Maximum</span>
                      <input
                        aria-label={`${selectedItem.label} maximum label`}
                        value={override.maxLabel ?? selectedItem.rasterRamp.maxLabel}
                        onChange={(event) =>
                          patchItem({ maxLabel: event.target.value })
                        }
                        style={field}
                      />
                    </label>
                    <label style={row}>
                      <span>Minimum</span>
                      <input
                        aria-label={`${selectedItem.label} minimum label`}
                        value={override.minLabel ?? selectedItem.rasterRamp.minLabel}
                        onChange={(event) =>
                          patchItem({ minLabel: event.target.value })
                        }
                        style={field}
                      />
                    </label>
                    <label style={check}>
                      <input
                        type="checkbox"
                        aria-label="Reverse legend color ramp"
                        checked={override.reverseRamp === true}
                        onChange={(event) =>
                          patchItem({ reverseRamp: event.target.checked })
                        }
                      />
                      Reverse color ramp
                    </label>
                  </>
                ) : null}
              </Section>
            </>
          ) : (
            <p>No visible legend items.</p>
          )}
        </div>
      ) : null}

      {view === 'text' ? (
        <div role="tabpanel" aria-label="Legend text symbol">
          <label style={{ ...row, margin: `${tokens.space[3]}px 0` }}>
            <span>Text item</span>
            <select
              aria-label="Legend text symbol part"
              value={textPart}
              onChange={(event) =>
                setTextPart(event.target.value as LegendTextPart)
              }
              style={field}
            >
              {TEXT_PARTS.map((part) => (
                <option key={part.id} value={part.id}>{part.label}</option>
              ))}
            </select>
          </label>
          <LayoutTextSymbolPanel
            value={legend.textSymbols?.[textPart] ?? DEFAULT_LAYOUT_TEXT_SYMBOL}
            heading={
              TEXT_PARTS.find((part) => part.id === textPart)?.label
              ?? 'Legend Text'
            }
            testId="legend-text-symbol-panel"
            onApply={(textSymbol) =>
              onChange({ textPart, textSymbol })
            }
            onBack={() => setView('item')}
          />
        </div>
      ) : null}
    </aside>
  );
}
